import { nonUndefined, onlyUnique } from "Utility";
import {
  DriverTime,
  getSortedDriverFinishTimes,
  isDnf,
} from "model/Match/DriverTime";

export const CRASH_LONG_TIMEMS = 5000;
export const CRASH_PERCENTILE = 50;
export const CRASH_SHORT_TIMEMS = 2000;
export const LONG_TIMEMS = 90000;
export const MISTAKE_LONG_TIMEMS = 2000;
export const MISTAKE_PERCENTILE = 70;
export const MISTAKE_SHORT_TIMEMS = 500;
export const SHORT_TIMEMS = 20000;

export const BEST_HEADER = "Best";
export const CRASH_HEADER = "CRSH";
export const DNF_HEADER = "DNF";
export const MEDIAN_HEADER = "Median";
export const MISTAKE_HEADER = "MST";

export const BEST_TITLE = "Best - Fastest Time In Live Play";
export const CRASH_TITLE =
  "CRSH - Percentage of Rounds Perceived To Have Crashed (Includes DNFs)";
export const DNF_TITLE = "DNF - Percentage Of Rounds Did Not Finish";
export const MEDIAN_TITLE = "Median - Median Time In Live Play (Includes DNFs)";
export const MISTAKE_TITLE =
  "MST - Percentage of Rounds Perceived To Have Made A Mistake (Doesn't Include CRSH)";

export const statTimesBest = (
  times: DriverTime[],
  includeDnf: boolean = false
) => {
  if (times.length === 0) {
    return undefined;
  }

  const finishTimes = nonUndefined(times.map((t) => t.finishTimeMs)).filter(
    (t) => t > 0
  );

  if (includeDnf && finishTimes.length === 0 && times.some((t) => isDnf(t))) {
    return -1;
  }

  return !!finishTimes.length ? Math.min(...finishTimes) : undefined;
};

export const statTimesBestWithDriverIds = (
  times: DriverTime[],
  includeDnf: boolean = false
): { time: number | undefined; driverIds: string[] } => {
  const best = statTimesBest(times, includeDnf);
  if (!best) {
    return { time: undefined, driverIds: [] };
  }

  const driverIds = times
    .filter((t) => t.finishTimeMs === best)
    .flatMap((t) => t.driverId)
    .filter(onlyUnique);

  return { time: best, driverIds };
};

export const statTimesMedian = (times: DriverTime[]) => {
  const finishTimes = getSortedDriverFinishTimes(times);
  if (finishTimes.length === 0) {
    return undefined;
  }

  const numberOfTimes = finishTimes.length;
  const half = Math.floor(numberOfTimes / 2);

  if (numberOfTimes % 2 === 0) {
    const time1 = finishTimes[half - 1];
    const time2 = finishTimes[half];

    if (time2 === -1) {
      return time1;
    }

    return Math.floor((time1 + time2) / 2);
  }

  return finishTimes[half];
};

export const statTimesPercentile = (
  times: DriverTime[],
  percentile: number
) => {
  const finishTimes = getSortedDriverFinishTimes(times);
  if (finishTimes.length === 0) {
    return undefined;
  }

  const numberOfTimes = finishTimes.length;
  const percentileIndex = Math.max(
    0,
    Math.min(
      numberOfTimes - 1,
      Math.floor((1 - percentile / 100) * numberOfTimes)
    )
  );

  if (numberOfTimes % 2 === 0 && percentileIndex > 0) {
    const time1 = finishTimes[percentileIndex - 1];
    const time2 = finishTimes[percentileIndex];

    if (time2 === -1) {
      return time1;
    }

    return Math.floor((time1 + time2) / 2);
  }

  return finishTimes[percentileIndex];
};

export const statTimesDnfCount = (times: DriverTime[]) => {
  return times.filter(isDnf).length;
};

export const statTimesDnfRate = (times: DriverTime[]) => {
  if (times.length === 0) {
    return { rate: undefined, breakdown: "0/0" };
  }

  const numberOfTimes = times.length;
  const numberOfDnf = statTimesDnfCount(times);

  return {
    rate: numberOfDnf / numberOfTimes,
    breakdown: `${numberOfDnf}/${numberOfTimes}`,
  };
};

export const statTimesThreshold = (
  bestTime: number,
  shortTrackDelta: number,
  longTrackDelta: number,
  times: DriverTime[],
  percentile: number
) => {
  const timePercentile = statTimesPercentile(times, percentile);
  if (timePercentile === undefined) {
    return undefined;
  }

  if (timePercentile === -1) {
    return -1;
  }

  const delta = Math.round(
    ((bestTime - SHORT_TIMEMS) / (LONG_TIMEMS - SHORT_TIMEMS)) *
      (longTrackDelta - shortTrackDelta) +
      shortTrackDelta
  );
  const timeDelta = bestTime + delta;
  return Math.max(timePercentile, timeDelta);
};

export const statTimesMistakeRate = (
  bestTime: number | undefined,
  times: DriverTime[],
  allTimes: DriverTime[]
) => {
  if (times.length === 0 || bestTime === undefined) {
    return { rate: undefined, breakdown: "0/0" };
  }
  const crashTime = statTimesThreshold(
    bestTime,
    CRASH_SHORT_TIMEMS,
    CRASH_LONG_TIMEMS,
    allTimes,
    CRASH_PERCENTILE
  );
  const mistakeTime = statTimesThreshold(
    bestTime,
    MISTAKE_SHORT_TIMEMS,
    MISTAKE_LONG_TIMEMS,
    allTimes,
    MISTAKE_PERCENTILE
  );
  if (crashTime === undefined || mistakeTime === undefined) {
    return { rate: undefined, breakdown: "0/0" };
  }
  const numberOfTimes = times.length;
  const numberOfMistakes = times.filter((t) => {
    if (mistakeTime === -1) {
      return isDnf(t);
    } else if (crashTime === -1) {
      return t.finishTimeMs !== undefined && t.finishTimeMs >= mistakeTime;
    } else {
      return (
        t.finishTimeMs !== undefined &&
        t.finishTimeMs >= mistakeTime &&
        t.finishTimeMs < crashTime
      );
    }
  }).length;

  return {
    rate: numberOfMistakes / numberOfTimes,
    breakdown: `${numberOfMistakes}/${numberOfTimes}`,
  };
};

export const statTimesCrashRate = (
  bestTime: number | undefined,
  times: DriverTime[],
  allTimes: DriverTime[]
) => {
  if (times.length === 0 || bestTime === undefined) {
    return { rate: undefined, breakdown: "0/0" };
  }
  const crashTime = statTimesThreshold(
    bestTime,
    CRASH_SHORT_TIMEMS,
    CRASH_LONG_TIMEMS,
    allTimes,
    CRASH_PERCENTILE
  );
  if (crashTime === undefined) {
    return { rate: undefined, breakdown: "0/0" };
  }
  const numberOfTimes = times.length;
  const numberOfCrashes = times.filter((t) => {
    if (crashTime === -1) {
      return isDnf(t);
    } else {
      return (
        t.finishTimeMs !== undefined &&
        (t.finishTimeMs >= crashTime || isDnf(t))
      );
    }
  }).length;

  return {
    rate: numberOfCrashes / numberOfTimes,
    breakdown: `${numberOfCrashes}/${numberOfTimes}`,
  };
};
